import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiShoppingBag } from "react-icons/fi";
import useMerchants from "../../../marketplace_admin/libs/useMerchants";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import StatCard from "../../components/StatCard";
import YesNoBadge from "../../components/YesNoBadge";
import useInfiniteScrollRef from "../../utils/useInfiniteScrollRef";
import { formatNgn } from "../../../Routes/Marketplace/libs/orderDisplayUtils";
import Spinner from "../../../Spinner/Spinner";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "—");

const FILTERS = [
  { id: "all", label: "All" },
  { id: "verified", label: "Verified" },
  { id: "pending", label: "Pending" },
  { id: "blocked", label: "Blocked" },
];

const AdminMerchantsPage = () => {
  const { data, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useMerchants();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const lastRef = useInfiniteScrollRef(fetchNextPage, hasNextPage, isFetchingNextPage);

  const merchants = useMemo(
    () => data?.pages?.flatMap((page) => page.merchants || []) || [],
    [data]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return merchants.filter((m) => {
      const name = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
      const matchesSearch =
        !term ||
        name.includes(term) ||
        (m.email || "").toLowerCase().includes(term);
      if (!matchesSearch) return false;
      if (filter === "verified") return m.isInfoVerify;
      if (filter === "pending") return !m.isInfoVerify;
      if (filter === "blocked") return m.blocked;
      return true;
    });
  }, [merchants, search, filter]);

  const stats = useMemo(
    () => ({
      total: merchants.length,
      verified: merchants.filter((m) => m.isInfoVerify).length,
      pending: merchants.filter((m) => !m.isInfoVerify).length,
      blocked: merchants.filter((m) => m.blocked).length,
    }),
    [merchants]
  );

  const columns = [
    {
      key: "name",
      label: "Merchant",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}
          </div>
          <div style={{ fontSize: 13, color: "var(--dash-muted)" }}>{r.email}</div>
        </div>
      ),
    },
    {
      key: "businessName",
      label: "Store",
      render: (r) => r.businessName || "—",
    },
    {
      key: "products",
      label: "Products",
      render: (r) => r.productCount ?? r.products?.length ?? 0,
    },
    {
      key: "pendingApproval",
      label: "Pending approval",
      render: (r) => r.pendingApprovalCount ?? 0,
    },
    {
      key: "orders",
      label: "Orders",
      render: (r) => r.orderCount ?? 0,
    },
    {
      key: "sales",
      label: "Total sales",
      render: (r) => formatNgn(r.totalSales || 0),
    },
    {
      key: "delivered",
      label: "Delivered",
      render: (r) => r.deliveredCount ?? 0,
    },
    {
      key: "registeredAt",
      label: "Registered",
      render: (r) => formatDate(r.registeredAt || r.createdAt),
    },
    {
      key: "verified",
      label: "Verified",
      render: (r) => (
        <YesNoBadge value={r.isInfoVerify} trueText="Verified" falseText="Pending" falseVariant="pending" />
      ),
    },
    {
      key: "active",
      label: "Active",
      render: (r) => <YesNoBadge value={r.isActive} />,
    },
    {
      key: "blocked",
      label: "Blocked",
      render: (r) => <YesNoBadge value={r.blocked} variant="blocked" />,
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="dashboard-table-actions">
          <Link to={`/admin/merchants/${r._id}`} className="dashboard-btn dashboard-btn--secondary">
            Details
          </Link>
          <Link to={`/admin/merchants/${r._id}/products`} className="dashboard-btn dashboard-btn--primary">
            Products
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Merchants"
        subtitle="Manage marketplace stores and verification status"
        icon={FiShoppingBag}
        actions={
          stats.pending > 0 ? (
            <Link to="/admin/verification" className="dashboard-btn dashboard-btn--primary">
              {stats.pending} pending review
            </Link>
          ) : null
        }
      />

      {!isLoading && merchants.length > 0 && (
        <div className="dashboard-stat-grid">
          <StatCard label="Loaded merchants" value={stats.total} />
          <StatCard label="Verified" value={stats.verified} />
          <StatCard label="Pending" value={stats.pending} />
          <StatCard label="Blocked" value={stats.blocked} />
        </div>
      )}

      <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search by name or email…">
        <div className="dashboard-filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={filter === f.id ? "active" : ""}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </FilterBar>

      {error && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load merchants.</p>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          icon={FiShoppingBag}
          title="No merchants found"
          message={search || filter !== "all" ? "Try a different search or filter." : "No merchants registered yet."}
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered.map((m) => ({ ...m, _key: m._id }))}
          loading={isLoading}
        />
      )}

      {!isLoading && filtered.length > 0 && (
        <div ref={lastRef} className="dashboard-load-more">
          {isFetchingNextPage && <Spinner />}
          {!hasNextPage && !isFetchingNextPage && (
            <span style={{ color: "var(--dash-muted)", fontSize: 13 }}>All merchants loaded</span>
          )}
        </div>
      )}
    </>
  );
};

export default AdminMerchantsPage;
